import React from "react";
import { t } from "i18next";

// Lightweight, dependency-free product-tour / walkthrough engine.
// Usage: <Tour open={bool} steps={[{selector, title, body, placement, action}]} onClose={fn} />
// - selector: CSS selector of the element to spotlight (optional; centered tooltip if absent/missing)
// - placement: 'bottom' | 'top' | 'left' | 'right' (auto if omitted)
// - action: optional fn run BEFORE the step is shown (e.g. open a panel so its target exists)
// Zero new npm deps, so it can't break the old CRA/webpack build.
class Tour extends React.Component {
  constructor(props) {
    super(props);
    this.state = { i: 0, rect: null };
  }

  componentDidMount() {
    if (this.props.open) this.go(0);
    window.addEventListener("resize", this.reposition);
    window.addEventListener("scroll", this.reposition, true);
    window.addEventListener("keydown", this.onKey);
  }
  componentDidUpdate(prev) {
    if (this.props.open && !prev.open) this.go(0);
  }
  componentWillUnmount() {
    window.removeEventListener("resize", this.reposition);
    window.removeEventListener("scroll", this.reposition, true);
    window.removeEventListener("keydown", this.onKey);
  }

  onKey = (e) => {
    if (!this.props.open) return;
    if (e.key === "Escape") this.finish();
    else if (e.key === "ArrowRight") this.next();
    else if (e.key === "ArrowLeft") this.prev();
  };

  go = (i) => {
    const steps = this.props.steps || [];
    if (i < 0) return;
    if (i >= steps.length) { this.finish(); return; }
    const step = steps[i];
    const show = () => this.setState({ i }, this.locate);
    if (step && typeof step.action === "function") {
      try { step.action(); } catch (e) { /* ignore */ }
      setTimeout(show, 80); // let React re-render the revealed target
    } else {
      show();
    }
  };

  locate = () => {
    const steps = this.props.steps || [];
    const step = steps[this.state.i] || {};
    const el = step.selector ? document.querySelector(step.selector) : null;
    if (el && el.scrollIntoView) {
      try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) { el.scrollIntoView(); }
    }
    setTimeout(() => {
      const node = step.selector ? document.querySelector(step.selector) : null;
      this.setState({ rect: node ? node.getBoundingClientRect() : null });
    }, el ? 240 : 0);
  };

  reposition = () => { if (this.props.open) this.locate(); };
  next = () => this.go(this.state.i + 1);
  prev = () => this.go(this.state.i - 1);
  finish = () => { if (this.props.onClose) this.props.onClose(); };

  tipStyle(rect, placement) {
    const W = 300, base = {
      position: "fixed", zIndex: 100001, width: W, maxWidth: "90vw", background: "#fff",
      border: "1px solid #e3e9e5", borderRadius: 12, boxShadow: "0 12px 38px rgba(0,0,0,.24)", padding: "14px 16px",
    };
    if (!rect) return Object.assign(base, { left: "50%", top: "50%", transform: "translate(-50%,-50%)" });
    const gap = 12, vw = window.innerWidth, vh = window.innerHeight;
    let place = placement;
    if (!place) place = (rect.bottom + 190 < vh) ? "bottom" : (rect.top > 200 ? "top" : "bottom");
    const clampL = (l) => Math.min(Math.max(l, 12), vw - W - 12);
    let left, top;
    if (place === "top") { top = Math.max(rect.top - gap - 170, 12); left = clampL(rect.left); }
    else if (place === "right") { left = Math.min(rect.right + gap, vw - W - 12); top = Math.min(Math.max(rect.top, 12), vh - 190); }
    else if (place === "left") { left = Math.max(rect.left - W - gap, 12); top = Math.min(Math.max(rect.top, 12), vh - 190); }
    else { top = Math.min(rect.bottom + gap, vh - 190); left = clampL(rect.left); }
    return Object.assign(base, { left, top });
  }

  render() {
    if (!this.props.open) return null;
    const steps = this.props.steps || [];
    const step = steps[this.state.i] || {};
    const rect = this.state.rect;
    const pad = 8;

    const spot = rect ? {
      position: "fixed", left: rect.left - pad, top: rect.top - pad,
      width: rect.width + pad * 2, height: rect.height + pad * 2, borderRadius: 12,
      boxShadow: "0 0 0 9999px rgba(15,25,20,.55)", border: "2px solid #2f9e44",
      zIndex: 100000, pointerEvents: "none", transition: "all .2s ease",
    } : null;

    const linkBtn = { border: 0, background: "transparent", color: "#87948c", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", padding: 4 };
    const ghostBtn = { border: "1px solid #cfd8d3", background: "#fff", color: "#54635c", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer", borderRadius: 8, padding: "7px 13px" };
    const primBtn = { border: 0, background: "#2f9e44", color: "#fff", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer", borderRadius: 8, padding: "7px 15px" };
    const last = this.state.i === steps.length - 1;

    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 99999 }}>
        {!rect && <div style={{ position: "fixed", inset: 0, background: "rgba(15,25,20,.55)" }} onClick={this.finish} />}
        {rect && <div style={spot} />}
        <div style={this.tipStyle(rect, step.placement)}>
          <div style={{ fontSize: 11, color: "#2f8a45", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".08em", fontVariantNumeric: "tabular-nums" }}>
            {(this.state.i + 1) + " / " + steps.length}
          </div>
          <div style={{ fontWeight: 700, fontSize: 15, margin: "4px 0 6px", color: "#18201d" }}>{step.title}</div>
          <div style={{ fontSize: 13.5, color: "#54635c", lineHeight: 1.5 }}>{step.body}</div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, gap: 8 }}>
            <button onClick={this.finish} style={linkBtn}>{t("Skip")}</button>
            <div style={{ display: "flex", gap: 8 }}>
              {this.state.i > 0 && <button onClick={this.prev} style={ghostBtn}>{t("Back")}</button>}
              <button onClick={this.next} style={primBtn}>{last ? t("Done") : t("Next")}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Tour;
